/**
 * Keep in sync with the inline script in src/index.html (localStorage key).
 */
export const LAYOUT_CONFIG_STORAGE_KEY = 'central-command-layout-config';

export interface AppConfig {
  inputStyle: string;
  colorScheme: string;
  theme: string;
  menuMode: string;
  scale: number;
}

const SCALE_MIN = 12;
const SCALE_MAX = 16;

export const LAYOUT_CONFIG_DEFAULTS: AppConfig = {
  inputStyle: 'outlined',
  menuMode: 'static',
  colorScheme: 'dark',
  theme: 'bootstrap4-dark-purple',
  scale: 14,
};

export function readStoredLayoutConfig(): Partial<AppConfig> | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  try {
    const raw = localStorage.getItem(LAYOUT_CONFIG_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as unknown;
    if (
      parsed === null ||
      typeof parsed !== 'object' ||
      Array.isArray(parsed)
    ) {
      return null;
    }
    return parsed as Partial<AppConfig>;
  } catch {
    return null;
  }
}

export function writeStoredLayoutConfig(config: AppConfig): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(LAYOUT_CONFIG_STORAGE_KEY, JSON.stringify(config));
  } catch {
    // Quota, private mode, etc.
  }
}

function clampScale(n: number): number {
  return Math.min(SCALE_MAX, Math.max(SCALE_MIN, Math.round(n)));
}

export function mergeLayoutConfigWithDefaults(
  defaults: AppConfig,
  stored: Partial<AppConfig> | null,
): AppConfig {
  if (!stored) {
    return { ...defaults };
  }

  const menuModes = new Set(['static', 'overlay']);
  const inputStyles = new Set(['outlined', 'filled']);
  const colorSchemes = new Set(['light', 'dark']);

  return {
    theme:
      typeof stored.theme === 'string' && stored.theme.trim() !== ''
        ? stored.theme
        : defaults.theme,
    colorScheme:
      typeof stored.colorScheme === 'string' &&
      colorSchemes.has(stored.colorScheme)
        ? stored.colorScheme
        : defaults.colorScheme,
    menuMode:
      typeof stored.menuMode === 'string' && menuModes.has(stored.menuMode)
        ? stored.menuMode
        : defaults.menuMode,
    inputStyle:
      typeof stored.inputStyle === 'string' && inputStyles.has(stored.inputStyle)
        ? stored.inputStyle
        : defaults.inputStyle,
    scale:
      typeof stored.scale === 'number' && !Number.isNaN(stored.scale)
        ? clampScale(stored.scale)
        : defaults.scale,
  };
}
